import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PageHeader, PageBody, Field } from "@/components/PageChrome";
import { Button, Input, Select } from "@/components/controls";
import { Sparkles, TrendingDown, PackageOpen, Package, AlertTriangle, Truck, X } from "lucide-react";
import { T } from "@/lib/testIds";
import ForecastButton from "@/components/ForecastButton";
import ImageThumb from "@/components/ImageThumb";

const TYPE_META = {
  restock: { icon: Package, label: "Restock", color: "border-l-blue-600 bg-blue-50", text: "text-blue-900", chip: "bg-blue-600" },
  liquidate: { icon: TrendingDown, label: "Liquidate", color: "border-l-amber-500 bg-amber-50", text: "text-amber-900", chip: "bg-amber-500" },
  slow_moving: { icon: PackageOpen, label: "Slow Moving", color: "border-l-slate-500 bg-slate-50", text: "text-slate-900", chip: "bg-slate-500" },
  overstock_risk: { icon: AlertTriangle, label: "Overstock Risk", color: "border-l-rose-500 bg-rose-50", text: "text-rose-900", chip: "bg-rose-500" },
};

const SEV_META = {
  high: { label: "HIGH", cls: "bg-red-100 text-red-800 border-red-200" },
  medium: { label: "MED", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  low: { label: "LOW", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

export default function Suggestions() {
  const [suggestions, setSuggestions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sourceOpts, setSourceOpts] = useState(["Direct", "Sea"]);
  // "Add to Procurement" modal state — null when hidden, an object when
  // showing the confirm dialog for a suggestion card.
  const [procureModal, setProcureModal] = useState(null);
  const [procureBusy, setProcureBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/suggestions").then((r) => setSuggestions(r.data.suggestions))
      .catch((e) => toast.error(formatApiError(e)));
    // Load Direct / Sea (+ custom) options for the modal's Source dropdown.
    api.get("/settings/dropdowns").then((r) => {
      const opts = (r.data || {}).procurement_source;
      if (Array.isArray(opts) && opts.length) setSourceOpts(opts);
    }).catch(() => { /* fall back to Direct/Sea default */ });
  }, []);

  const grouped = suggestions.reduce((acc, s) => {
    acc[s.type] = acc[s.type] || [];
    acc[s.type].push(s);
    return acc;
  }, {});

  const filtered = filter === "all" ? suggestions : suggestions.filter((s) => s.type === filter);

  const FILTERS = [
    { k: "all", label: "All", count: suggestions.length },
    { k: "restock", label: "Restock", count: (grouped.restock || []).length },
    { k: "liquidate", label: "Liquidate", count: (grouped.liquidate || []).length },
    { k: "slow_moving", label: "Slow Moving", count: (grouped.slow_moving || []).length },
    { k: "overstock_risk", label: "Overstock", count: (grouped.overstock_risk || []).length },
  ];

  return (
    <>
      <PageHeader title="Auto Suggestions" subtitle="Sales-trend-driven actions for your inventory" testId={T.moduleTitle("suggestions")} module="suggestions">
        <div className="flex items-center gap-1.5 h-8 px-3 bg-gradient-to-r from-violet-600 to-indigo-700 text-white rounded-sm text-[12px] font-semibold">
          <Sparkles size={13} /> {suggestions.length} insights
        </div>
      </PageHeader>
      <PageBody>
        <div className="flex gap-2 mb-4 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.k}
              data-testid={`sugg-filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`h-8 px-3 text-[12px] border rounded-sm transition-colors ${filter === f.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
            >
              {f.label} <span className="ml-1 font-mono opacity-70">({f.count})</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((s) => {
            const meta = TYPE_META[s.type] || TYPE_META.slow_moving;
            const Icon = meta.icon;
            return (
              <div key={s.id} data-testid={`sugg-${s.id}`} className={`bg-white border border-slate-200 border-l-4 ${meta.color.split(" ")[0]} rounded-sm p-4 flex gap-3`}>
                {s.image_url ? (
                  <ImageThumb src={s.image_url} size={64} className="shrink-0" testId={`sugg-thumb-${s.id}`} />
                ) : (
                  <div className="w-16 h-16 border border-slate-200 rounded-sm bg-slate-100 flex items-center justify-center shrink-0"><Icon size={20} className="text-slate-500" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold text-white px-1.5 py-0.5 rounded-sm ${meta.chip}`}>
                      <Icon size={10} /> {meta.label}
                    </span>
                    <span className={`inline-flex text-[10px] font-bold px-1.5 py-0.5 rounded-sm border ${SEV_META[s.severity]?.cls || SEV_META.low.cls}`}>
                      {SEV_META[s.severity]?.label || s.severity.toUpperCase()}
                    </span>
                    <span className="text-[11px] text-slate-500 font-mono truncate">{s.warehouse_name || "—"}</span>
                  </div>
                  <div className="font-heading text-[13.5px] font-semibold text-slate-900 truncate">{s.product_name}</div>
                  <div className="text-[11.5px] text-slate-500 font-mono mb-2">{s.system_sku}</div>
                  <div className="text-[12px] text-slate-700 mb-2">{s.reason}</div>
                  {/* Feb 27 2026 — 30 / 60 / 90 day breakdown incl. total
                      + refund count/qty. Only rendered when the suggestion
                      carries these fields (all types except pure overstock
                      already do). */}
                  {(s.sales_last_30d !== undefined
                    || s.sales_last_60d !== undefined
                    || s.sales_last_90d !== undefined) && (
                    <div className="mb-2 border border-slate-200 rounded-sm overflow-hidden" data-testid={`sugg-sales-grid-${s.id}`}>
                      <table className="w-full text-[11px]">
                        <thead className="bg-slate-50 text-slate-500">
                          <tr>
                            <th className="text-left px-2 py-1 font-medium">Period</th>
                            <th className="text-right px-2 py-1 font-medium">Sales</th>
                            <th className="text-right px-2 py-1 font-medium">Total</th>
                            <th className="text-right px-2 py-1 font-medium">Refunded</th>
                          </tr>
                        </thead>
                        <tbody className="font-mono">
                          {[
                            ["30d", s.sales_last_30d, s.total_sales_30d, s.refunded_count_30d, s.refunded_qty_30d],
                            ["60d", s.sales_last_60d, s.total_sales_60d, s.refunded_count_60d, s.refunded_qty_60d],
                            ["90d", s.sales_last_90d, s.total_sales_90d, s.refunded_count_90d, s.refunded_qty_90d],
                          ].map(([lbl, sales, total, rcnt, rqty]) => (
                            <tr key={lbl} className="border-t border-slate-100" data-testid={`sugg-period-${s.id}-${lbl}`}>
                              <td className="px-2 py-0.5 text-slate-600">{lbl}</td>
                              <td className="text-right px-2 py-0.5 font-semibold">{sales ?? 0}</td>
                              <td className="text-right px-2 py-0.5">{total ?? 0}</td>
                              <td className="text-right px-2 py-0.5 text-rose-700">
                                {(rcnt ?? 0)}{(rqty ?? 0) ? ` · ${rqty}u` : ""}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 text-[11.5px]">
                    {s.current_qty !== undefined && <span><span className="text-slate-500">Stock:</span> <span className="font-mono font-semibold">{s.current_qty}</span></span>}
                    {s.in_transit !== undefined && <span><span className="text-slate-500">In-Transit:</span> <span className="font-mono font-semibold">{s.in_transit}</span></span>}
                    {s.days_of_cover !== undefined && <span><span className="text-slate-500">Cover:</span> <span className="font-mono font-semibold">{s.days_of_cover}d</span></span>}
                    {s.suggested_reorder_qty !== undefined && <span><span className="text-slate-500">Reorder:</span> <span className="font-mono font-bold text-blue-700">{s.suggested_reorder_qty} units</span></span>}
                    {s.days_since_last_sale !== undefined && <span><span className="text-slate-500">Idle:</span> <span className="font-mono font-semibold">{s.days_since_last_sale}d</span></span>}
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <Button onClick={() => navigate(`/inventory?sku=${s.system_sku}`)}>Open inventory</Button>
                    {s.system_sku && (
                      <ForecastButton sku={s.system_sku} testId={`sugg-forecast-${s.id}`} />
                    )}
                    {s.type === "restock" && (
                      <Button
                        variant="primary"
                        data-testid={`sugg-procure-${s.id}`}
                        testId={`sugg-procure-${s.id}`}
                        onClick={() => setProcureModal({
                          suggestion: s,
                          qty: s.suggested_reorder_qty ?? 1,
                          procurement_source: sourceOpts[0] || "Direct",
                        })}
                      >
                        <Truck size={12} /> Add to Procurement
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {!filtered.length && <div className="col-span-2 text-center py-10 text-slate-500">No suggestions right now — inventory looks healthy.</div>}
        </div>
      </PageBody>
      {procureModal && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-[2px] flex items-center justify-center p-4"
          data-testid="sugg-procure-modal"
          onClick={() => !procureBusy && setProcureModal(null)}
        >
          <div
            className="bg-white rounded-sm shadow-xl w-full max-w-md border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div>
                <div className="font-heading text-[14px] font-semibold text-slate-900 flex items-center gap-1.5">
                  <Truck size={14} className="text-blue-700" /> Add to Procurement
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{procureModal.suggestion.system_sku}</div>
              </div>
              <button
                onClick={() => !procureBusy && setProcureModal(null)}
                className="text-slate-500 hover:text-slate-900"
                data-testid="sugg-procure-modal-close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-4 grid grid-cols-1 gap-3">
              <Field label="Quantity to add">
                <Input
                  type="number"
                  testId="sugg-procure-qty"
                  value={procureModal.qty ?? ""}
                  onChange={(e) => setProcureModal((s) => ({ ...s, qty: e.target.value === "" ? "" : Number(e.target.value) }))}
                  min={1}
                />
              </Field>
              <Field label="Source">
                <Select
                  testId="sugg-procure-source"
                  options={sourceOpts}
                  value={procureModal.procurement_source}
                  onChange={(v) => setProcureModal((s) => ({ ...s, procurement_source: v }))}
                  allowEmpty={false}
                />
              </Field>
              <div className="text-[11px] text-slate-500">
                Creates a new procurement entry with status <span className="font-mono font-semibold">Eligible</span>.
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
              <Button
                onClick={() => setProcureModal(null)}
                disabled={procureBusy}
                testId="sugg-procure-cancel"
              >Cancel</Button>
              <Button
                variant="primary"
                disabled={procureBusy || !procureModal.qty || Number(procureModal.qty) <= 0}
                data-testid="sugg-procure-confirm"
                testId="sugg-procure-confirm"
                onClick={async () => {
                  const s = procureModal.suggestion;
                  setProcureBusy(true);
                  try {
                    await api.post("/procurement/from-suggestion", {
                      system_sku: s.system_sku,
                      product_name: s.product_name,
                      image_url: s.image_url,
                      warehouse_name: s.warehouse_name,
                      qty: procureModal.qty,
                      reason: s.reason,
                      procurement_source: procureModal.procurement_source,
                    });
                    toast.success(`${s.system_sku} added to Procurement (${procureModal.procurement_source})`);
                    setProcureModal(null);
                    navigate("/procurement");
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setProcureBusy(false); }
                }}
              >
                <Truck size={12} /> Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
